import { lazy, Suspense, useLayoutEffect, useRef, useState } from 'react'

import { useOverlayContentLayout } from '@/hooks/useOverlayContentLayout'
import { useWidgetStore } from '@/stores/useWidgetStore'
import { useWidgetBreakpointSettings } from '../hooks/useWidgetBreakpointSettings'
import { getPositionStyle, resolveIconPadding } from '../utils/widget-utils'
import { OverlayLayoutProvider } from './widget-overlay/OverlayLayoutContext'
import WidgetIcon from './widget-overlay/WidgetIcon'

const LoopPlacement = lazy(() => import('./widget-overlay/LoopPlacement'))
const WelcomeMessage = lazy(() => import('./widget-overlay/WelcomeMessage'))

const CONTENT_GAP = 8

export default function WidgetOverlay() {
  const floatingWidgetOpen = useWidgetStore((state) => state.floatingWidgetOpen)
  const overlayPosition = useWidgetStore((state) => state.overlayPosition)
  const isOverlayDragging = useWidgetStore((state) => state.isOverlayDragging)
  const { iconSettings } = useWidgetBreakpointSettings()

  const contentRef = useRef<HTMLDivElement>(null)
  const [contentSize, setContentSize] = useState({ width: 0, height: 0 })

  useLayoutEffect(() => {
    const el = contentRef.current
    if (!el) return

    const measure = () => {
      const rect = el.getBoundingClientRect()
      setContentSize((prev) => {
        if (Math.abs(prev.width - rect.width) < 1 && Math.abs(prev.height - rect.height) < 1) {
          return prev
        }
        return { width: rect.width, height: rect.height }
      })
    }
    measure()

    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const layout = useOverlayContentLayout(contentSize)
  const { contentAbove, contentOnRight, iconWidth, iconHeight } = layout

  const position = iconSettings?.position ?? 'bottom-left'
  const positionStyle = getPositionStyle(position)
  const padding = resolveIconPadding(iconSettings?.padding)

  const offsetStyle = {
    ...positionStyle,
    ...(positionStyle.top !== undefined && { top: padding.top }),
    ...(positionStyle.bottom !== undefined && { bottom: padding.bottom }),
    ...(positionStyle.left !== undefined && { left: padding.left }),
    ...(positionStyle.right !== undefined && { right: padding.right }),
  }

  // If widget is open, we just don't show the overlay
  if (floatingWidgetOpen) {
    return null
  }

  return (
    <OverlayLayoutProvider value={layout}>
      <div
        style={{
          position: 'fixed',
          ...offsetStyle,
          zIndex: (iconSettings?.zIndex || 2147483647) - 1,
          pointerEvents: 'none',
          width: iconWidth,
          height: iconHeight,
          transform: `translate(${overlayPosition.x}px, ${overlayPosition.y}px)`,
          transition: isOverlayDragging ? 'none' : undefined,
        }}
      >
        <div style={{ position: 'relative', width: iconWidth, height: iconHeight }}>
          <div
            ref={contentRef}
            style={{
              position: 'absolute',
              zIndex: 1,
              display: 'flex',
              // Stable child order — reverse when above so welcome stays next to the icon
              // without remounting (remounts re-trigger intro animations and flicker)
              flexDirection: contentAbove ? 'column-reverse' : 'column',
              gap: CONTENT_GAP,
              alignItems: contentOnRight ? 'flex-start' : 'flex-end',
              // Avoid shrink-to-fit against the icon-sized containing block
              width: 'max-content',
              ...(contentOnRight ? { left: iconWidth } : { right: iconWidth }),
              ...(contentAbove ? { bottom: iconHeight + CONTENT_GAP } : { top: iconHeight + CONTENT_GAP }),
            }}
          >
            <Suspense>
              <WelcomeMessage />
            </Suspense>
            <Suspense>
              <LoopPlacement />
            </Suspense>
          </div>

          <div style={{ position: 'relative', zIndex: 2 }}>
            <WidgetIcon />
          </div>
        </div>
      </div>
    </OverlayLayoutProvider>
  )
}
